import React, { useEffect, useState, useMemo } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  CircularProgress,
  Paper,
} from '@material-ui/core';
import axios from 'axios';
import { useApi, identityApiRef, discoveryApiRef } from '@backstage/core-plugin-api';
import yaml from 'js-yaml';
import nunjucks from 'nunjucks';

// Configure nunjucks with custom delimiters
nunjucks.configure({
  autoescape: false,
  tags: {
    variableStart: '${{',
    variableEnd: '}}',
  },
});

export const SelectAdoRegion = (props: FieldExtensionComponentProps<object>) => {
  // Use the "value" prop for the field's current value
  const { onChange, value, required, rawErrors, uiSchema, formContext } = props;

  // 1. Pull raw strings from ui:options
  const adoProjectTemplate = uiSchema['ui:options']?.adoProject || '';
  const adoProductTemplate = uiSchema['ui:options']?.adoProduct || '';
  const overrideRegionTemplate = uiSchema['ui:options']?.override_region || 'false';

  // 2. Resolve templates via Nunjucks using full formContext
  const adoProject = useMemo(() => {
    const resolved = nunjucks.renderString(adoProjectTemplate, formContext.formData).trim();
    console.log('Resolved adoProject:', resolved);
    return resolved;
  }, [adoProjectTemplate, formContext.formData]);

  const adoProduct = useMemo(() => {
    const resolved = nunjucks.renderString(adoProductTemplate, formContext.formData).trim();
    console.log('Resolved adoProduct:', resolved);
    return resolved;
  }, [adoProductTemplate, formContext.formData]);

  const resolvedOverrideRegion = useMemo(() => {
    const rendered = nunjucks.renderString(overrideRegionTemplate, formContext.formData).trim();
    const boolVal = rendered.toLowerCase() === 'true';
    console.log('Resolved override_region:', boolVal);
    return boolVal;
  }, [overrideRegionTemplate, formContext.formData]);

  // 3. Local state: initialize regionMap from the current value (object expected)
  const [regionMap, setRegionMap] = useState<Record<string, string>>(
    (value as Record<string, string>) || {}
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [allowedEnvironments, setAllowedEnvironments] = useState<string[]>([]);
  const [data, setData] = useState<any>(null);
  // Flag to prevent re‑initialization after user updates a value.
  const [initialized, setInitialized] = useState(false);

  const identityApi = useApi(identityApiRef);
  const discoveryApi = useApi(discoveryApiRef);

  const [baseUrl, setBaseUrl] = useState('');

  useEffect(() => {
    const fetchBaseUrl = async () => {
      const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
      setBaseUrl(baseUrl);
    };
    fetchBaseUrl();
  }, [discoveryApi]);

  // 4. Sync local state when parent's value changes
  useEffect(() => {
    if (value && Object.keys(value).length > 0) {
      setRegionMap(value as Record<string, string>);
      console.log('Synced regionMap from value:', value);
    }
  }, [value]);

  // 4.5. Reset initialized flag if adoProject or adoProduct changes
  useEffect(() => {
    setInitialized(false);
  }, [adoProject, adoProduct]);

  // 5. Fetch YAML from AWS Parameter Store after adoProject and adoProduct are resolved
  useEffect(() => {
    if (!adoProject || !adoProduct) {
      setLoading(false);
      setError('Please select both an ADO Project and an ADO Product');
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const credentials = await identityApi.getCredentials();
        const token = credentials.token;
        const endpoint = `/backstage/project/${adoProject}`;
        console.log('Fetching YAML from:', endpoint);

        const response = await axios.post(
          `${baseUrl}/aws/params`,
          { name: endpoint },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        const rawValue = response.data.value || '';
        if (!rawValue) {
          throw new Error('No data returned from parameter store');
        }

        const parsed = yaml.load(rawValue) as any;
        if (!parsed || typeof parsed !== 'object') {
          throw new Error('Invalid YAML data');
        }
        console.log('Parsed YAML data:', parsed);
        setData(parsed);

        // Read allowed environments from the new structure
        const envs = parsed.allowed_environments || [];
        if (!Array.isArray(envs) || envs.length === 0) {
          throw new Error('No allowed environments found in YAML');
        }
        setAllowedEnvironments(envs);

        // Build default region mapping for each allowed environment using the environment block from parsed.environments.
        const defaultRegions: Record<string, string> = {};
        envs.forEach((env: string) => {
          const envObj = parsed.environments ? parsed.environments[env] || {} : {};
          defaultRegions[env] = envObj.default_aws_region || '';
        });
        console.log('Default regions mapping:', defaultRegions);

        // Only initialize if not already initialized (to avoid overwriting user changes)
        if (!initialized) {
          setRegionMap(defaultRegions);
          onChange({ ...defaultRegions });
          setInitialized(true);
        }
      } catch (err: any) {
        console.error('Error fetching YAML:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [adoProject, adoProduct, identityApi, onChange, initialized, baseUrl]);

  // 6. Handler for when user selects a different region
  const handleRegionChange = (env: string, region: string) => {
    const updated = { ...regionMap, [env]: region };
    setRegionMap(updated);
    onChange({ ...updated });
    console.log(`Region for ${env} updated to: ${region}`, updated);
  };

  // 7. Render UI based on state
  if (loading) {
    return (
      <FormControl fullWidth>
        <Box display="flex" justifyContent="center" padding={2}>
          <CircularProgress />
        </Box>
      </FormControl>
    );
  }

  if (error) {
    return (
      <FormControl fullWidth error>
        <FormHelperText>{error}</FormHelperText>
      </FormControl>
    );
  }

  if (!data) {
    return (
      <FormControl fullWidth>
        <FormHelperText>No data available</FormHelperText>
      </FormControl>
    );
  }

  // If override_region is false, display read-only defaults
  if (!resolvedOverrideRegion) {
    return (
      <FormControl fullWidth required={required} error={!!rawErrors?.length}>
        <Typography variant="h6">Default AWS Regions</Typography>
        <Paper>
          <Box padding={2}>
            {allowedEnvironments.map(env => (
              <Box key={env} marginBottom={1}>
                <Typography>
                  <strong>{env}:</strong> {regionMap[env] || 'Not set'}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      </FormControl>
    );
  }

  // If override_region is true, allow user to change regions
  return (
    <FormControl fullWidth required={required} error={!!rawErrors?.length}>
      <Typography variant="h6" gutterBottom>
        Select AWS Regions for Environments
      </Typography>
      {allowedEnvironments.map(env => {
        const envData = data.environments ? data.environments[env] || {} : {};
        // Copy allowedRegions array to avoid mutating parsed data
        const allowedRegions = Array.isArray(envData.allowed_aws_regions)
          ? [...envData.allowed_aws_regions]
          : [];
        const defaultRegion = envData.default_aws_region || '';

        // Ensure default region is included in allowedRegions
        if (defaultRegion && !allowedRegions.includes(defaultRegion)) {
          allowedRegions.push(defaultRegion);
        }

        return (
          <FormControl key={env} fullWidth margin="normal">
            <InputLabel id={`region-label-${env}`}>
              Environment: {env}
            </InputLabel>
            <Select
              labelId={`region-label-${env}`}
              value={regionMap[env] || defaultRegion}
              onChange={(e) => handleRegionChange(env, e.target.value as string)}
              label={`Environment: ${env}`}
            >
              {allowedRegions.map(r => (
                <MenuItem key={r} value={r}>
                  {r} {r === defaultRegion ? '(Default)' : ''}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {regionMap[env] === defaultRegion ? 'Using default region' : ''}
            </FormHelperText>
          </FormControl>
        );
      })}
    </FormControl>
  );
};
